import { FC, useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router";

import { Button, Buttons, Message, Messages, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";

import { createRenewalCodeAPI } from "@/api";
import { AuthenticationContext } from "@/context";
import { getDaysUntilExpiration, getThemeURL, isRenewalWindowOpen } from "@/utils";

interface PACaseRenewalBannerProps {
  caseId: number;
  expirationDate?: string;
  renewalWindowDate?: string;
  hasRenewalCode?: boolean;
  showRenewalInstructions?: boolean;
}

export const PACaseRenewalBanner: FC<PACaseRenewalBannerProps> = ({
  caseId,
  expirationDate,
  renewalWindowDate,
  hasRenewalCode = false,
  showRenewalInstructions = false,
}) => {
  const navigate = useNavigate();
  const showAlert = useAlert();
  const { getAuthToken } = useContext(AuthenticationContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const renewCase = useCallback(async () => {
    setIsLoading(true);

    try {
      const renewal = await createRenewalCodeAPI(
        { case_id: caseId },
        getAuthToken(),
      );
      const encodedParams = new URLSearchParams();
      encodedParams.append("prescriber_npi", renewal.npi);
      encodedParams.append("renewal_code", renewal.code);

      navigate({
        pathname: getThemeURL("PA_CREATE"),
        search: encodedParams.toString(),
      });
    } catch (error) {
      setIsLoading(false);
      showAlert({ type: "error", style: "banner", message: "This case cannot be renewed right now" });
    }
  }, [caseId, getAuthToken, navigate, showAlert]);

  if (hasRenewalCode || !isRenewalWindowOpen(renewalWindowDate, expirationDate)) {
    return null;
  }

  const daysUntilExpiration = getDaysUntilExpiration(expirationDate);
  const renewButton = (
    <Button
      appearance={Buttons.Functional.Tertiary.Compact.Unpadded}
      onClick={renewCase}
      disabled={isLoading}
      testId="test__pa_case_renew_now"
    >
      Renew Now
    </Button>
  );

  if (!showRenewalInstructions) {
    return (
      <Message appearance={Messages.Warning} visible testId="test__pa_case_renewal_banner">
        <Shelf alignment="center" spacing="small">
          <Body>
            This authorization is expiring in <Semibold>{daysUntilExpiration} days</Semibold>
          </Body>
          <Shelf.Spacer />
          {renewButton}
        </Shelf>
      </Message>
    );
  }

  return (
    <Message
      appearance={Messages.Warning}
      visible
      actionContent={renewButton}
      testId="test__pa_case_renewal_banner"
    >
      <Body>
        This authorization is expiring in <Semibold>{daysUntilExpiration} days</Semibold>. Renew now to avoid interruptions in treatment.
      </Body>
    </Message>
  );
};

export default PACaseRenewalBanner;
